/**
 * SQL 빌더용 토크나이저(2026-10-19) — 위치(start·end, 원문 글자 오프셋)를 가진 토큰.
 * 구조 스캐너가 절 경계·괄호·서브쿼리를 찾는 데만 쓴다 — 의미 분석은 하지 않는다.
 * 문자열('…', q'[…]'), 따옴표 식별자("…"), 주석(줄 주석 · 블록 주석)은 한 덩어리로 건너뛰어 안의 괄호·키워드에 속지 않게 한다.
 * 주석은 토큰으로 내지 않는다(원문은 그대로 남는다 — 바꾸기는 늘 오프셋으로 한다).
 *
 * @returns {{ tokens: Array<{ t: 'word'|'str'|'num'|'punct'|'bind', v: string, start: number, end: number, quoted?: boolean }>, errors: Array<{ code, pos }> }}
 */
export function tokenize(text) {
    const s = String(text || '');
    const n = s.length;
    const tokens = [];
    const errors = [];
    let i = 0;
    while (i < n) {
        const ch = s[i];
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v' || ch === ' ') { i++; continue; }
        // 주석
        if (ch === '-' && s[i + 1] === '-') { while (i < n && s[i] !== '\n') i++; continue; }
        if (ch === '/' && s[i + 1] === '*') {
            const p = i;
            i += 2;
            while (i < n && !(s[i] === '*' && s[i + 1] === '/')) i++;
            if (i >= n) { errors.push({ code: 'UNCLOSED_COMMENT', pos: p }); break; }
            i += 2;
            continue;
        }
        // q'[ … ]' — Oracle 대체 따옴표(N 접두 포함: nq'…')
        if ((ch === 'q' || ch === 'Q' || ((ch === 'n' || ch === 'N') && (s[i + 1] === 'q' || s[i + 1] === 'Q'))) ) {
            const qAt = (ch === 'n' || ch === 'N') ? i + 1 : i;
            if (s[qAt + 1] === "'" && s[qAt + 2]) {
                const p = i;
                const open = s[qAt + 2];
                const close = { '[': ']', '{': '}', '(': ')', '<': '>' }[open] || open;
                let k = qAt + 3;
                while (k < n && !(s[k] === close && s[k + 1] === "'")) k++;
                if (k >= n) { errors.push({ code: 'UNCLOSED_QUOTE', pos: p }); tokens.push({ t: 'str', v: s.slice(p), start: p, end: n }); break; }
                tokens.push({ t: 'str', v: s.slice(p, k + 2), start: p, end: k + 2 });
                i = k + 2;
                continue;
            }
        }
        // 'string' (안의 '' 는 따옴표 하나) — N'…' 도
        if (ch === "'" || ((ch === 'n' || ch === 'N') && s[i + 1] === "'")) {
            const p = i;
            i += ch === "'" ? 1 : 2;
            let closed = false;
            while (i < n) {
                if (s[i] === "'") { if (s[i + 1] === "'") { i += 2; continue; } closed = true; i++; break; }
                i++;
            }
            if (!closed) errors.push({ code: 'UNCLOSED_QUOTE', pos: p });
            tokens.push({ t: 'str', v: s.slice(p, i), start: p, end: i });
            continue;
        }
        // "따옴표 식별자"
        if (ch === '"') {
            const p = i;
            i++;
            while (i < n && s[i] !== '"') i++;
            if (i >= n) { errors.push({ code: 'UNCLOSED_QUOTE', pos: p }); tokens.push({ t: 'word', v: s.slice(p + 1), quoted: true, start: p, end: n }); break; }
            tokens.push({ t: 'word', v: s.slice(p + 1, i), quoted: true, start: p, end: i + 1 });
            i++;
            continue;
        }
        // 바인드 :name · :1 (PostgreSQL 의 :: 형변환은 기호)
        if (ch === ':' && s[i + 1] !== ':' && s[i + 1] !== '=' && /[A-Za-z0-9_$#\u0080-￿]/.test(s[i + 1] || '')) {
            const p = i;
            i++;
            while (i < n && /[A-Za-z0-9_$#\u0080-￿]/.test(s[i])) i++;
            tokens.push({ t: 'bind', v: s.slice(p, i), start: p, end: i });
            continue;
        }
        if (/[A-Za-z_\u0080-￿]/.test(ch)) {
            const p = i;
            while (i < n && /[A-Za-z0-9_$#\u0080-￿]/.test(s[i])) i++;
            tokens.push({ t: 'word', v: s.slice(p, i).toUpperCase(), start: p, end: i });
            continue;
        }
        if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(s[i + 1] || ''))) {
            const p = i;
            while (i < n && /[0-9.eE]/.test(s[i])) i++;
            tokens.push({ t: 'num', v: s.slice(p, i), start: p, end: i });
            continue;
        }
        tokens.push({ t: 'punct', v: ch, start: i, end: i + 1 });
        i++;
    }
    return { tokens, errors };
}
